import React, { useMemo, useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { createPortal } from "react-dom";
import styles from "./AddTagsComponent.module.css";

import { AuthAPI, TagsAPI } from "../../../../../utils/firebase";

// Use same separators/normalization as everywhere else
import {
  TAG_PATH_SEP,
  normalizeFullPath,
  segmentsFromPath,
} from "../../UploadDataComponent/utils/tagUtils";

export default function AddTagsComponent({ onClose, userEmail, existingPaths }) {
  // Leaf name (like Gmail "New label")
  const [name, setName] = useState("");
  // Nesting toggle + chosen parent
  const [nest, setNest] = useState(false);
  const [parentPath, setParentPath] = useState("");

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Build parent options from existing fullPaths (preserve encounter order)
  const parentOptions = useMemo(() => {
    const arr = Array.from(existingPaths || new Set());
    return arr.map((fp) => {
      const segs = segmentsFromPath(fp);
      const depth = segs.length;
      const leaf = depth ? segs[segs.length - 1] : fp;
      // subtle textual indent like the tree: '---' per depth-1
      const prefix = depth > 1 ? `${Array(depth - 1).fill("---").join(" ")} ` : "";
      return { value: fp, label: `${prefix}${leaf}`, depth };
    });
  }, [existingPaths]);

  // Full path preview we’re about to write
  const previewFullPath = useMemo(() => {
    const leaf = String(name || "").trim();
    if (!leaf) return "";
    const base = nest && parentPath ? `${parentPath}${TAG_PATH_SEP}${leaf}` : leaf;
    return normalizeFullPath(base);
  }, [name, nest, parentPath]);

  // Duplicate path check (compare against normalized set entries)
  const isDuplicate = useMemo(() => {
    if (!previewFullPath) return false;
    return existingPaths?.has(previewFullPath.trim()) ?? false;
  }, [existingPaths, previewFullPath]);

  const canSave = !!userEmail && !!previewFullPath && !isDuplicate && !saving;

  const handleOverlayMouseDown = useCallback(
    (e) => {
      if (e.target === e.currentTarget) onClose?.();
    },
    [onClose]
  );

  useEffect(() => {
    const body = document.body;
    const prevOverflow = body.style.overflow;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    body.classList.add("modal-open");
    body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      body.classList.remove("modal-open");
      body.style.overflow = prevOverflow || "";
    };
  }, [onClose]);

  const onSave = async () => {
    if (!canSave) return;

    try {
      setSaving(true);
      setError(null);

      const { auth } = AuthAPI.getFirebase();
      const u = auth?.currentUser;
      const user = u?.uid && u?.email ? { uid: u.uid, email: String(u.email).toLowerCase() } : null;

      if (!user) {
        throw new Error("Not signed in.");
      }

      // Create by name + optional parentPath (writer composes/normalizes fullPath)
      await TagsAPI.createUserTag({
        user,
        name: String(name).trim(),
        ...(nest && parentPath ? { parentPath } : {}),
        source: "ui-manual",
      });

      onClose?.();
    } catch (e) {
      console.error("[Tags] add tag error", e);
      setError(e?.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <>
      <div
        className="modal fade show d-block pp-modal"
        tabIndex="-1"
        role="dialog"
        aria-modal="true"
        onMouseDown={handleOverlayMouseDown}
        style={{ position: "fixed", inset: 0, zIndex: 1080 }}
      >
        <div className="modal-dialog modal-md modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">New tag</h5>
              <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
            </div>

            <div className="modal-body">
              {/* Tag name */}
              <div className="mb-3">
                <label className="form-label">Please enter a new tag name:</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g. Dates"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>

              {/* Nest toggle + parent select */}
              <div className={`form-check mb-2 ${styles.rowTight}`}>
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="nestTagCheckbox"
                  checked={nest}
                  onChange={(e) => setNest(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="nestTagCheckbox">
                  Nest tag under:
                </label>
              </div>

              <div className="mb-2">
                <select
                  className="form-select"
                  disabled={!nest}
                  value={parentPath}
                  onChange={(e) => setParentPath(e.target.value)}
                >
                  <option value="">— Select a parent —</option>
                  {parentOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <div className="form-text">
                  Parent tags are shown with subtle <code>---</code> indentation.
                </div>
              </div>

              {/* Preview & validation */}
              <div className="mt-2">
                <div className="small text-secondary">
                  Full path (preview):{" "}
                  <code className={styles.mono}>
                    {previewFullPath || "—"}
                  </code>
                </div>
                {isDuplicate && (
                  <div className="text-danger small mt-2">
                    A tag with this full path already exists.
                  </div>
                )}
                {error && <div className="text-danger small mt-2">{error}</div>}
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-outline-secondary" onClick={onClose} disabled={saving}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={onSave}
                type="button"
                disabled={!canSave}
              >
                {saving ? "Saving…" : "Create"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="modal-backdrop fade show pp-modal-backdrop" style={{ zIndex: 1070 }} />
    </>,
    document.body
  );
}

AddTagsComponent.propTypes = {
  onClose: PropTypes.func.isRequired,
  userEmail: PropTypes.string,
  existingPaths: PropTypes.instanceOf(Set),
};

AddTagsComponent.defaultProps = {
  userEmail: null,
  existingPaths: new Set(),
};
